// Port of webapp/src/widgets/editable.tsx — the click-to-edit text field used
// by card titles, view names, column headers and property values.

import {ChangeEvent, KeyboardEvent, ReactElement, RefObject, useEffect, useRef, useState} from 'react'
import classnames from 'classnames'

type EditableProps = {
    value: string
    onChange: (value: string) => void
    onActivate?: () => void
    placeholder?: string
    readOnly?: boolean
    multiline?: boolean
    className?: string
    inputRef?: RefObject<HTMLInputElement | HTMLTextAreaElement>
    spellCheck?: boolean

    /** commit on blur + Enter (single line) / blur + Ctrl+Enter (multiline) */
    allowEmpty?: boolean
}

export default function Editable({value, onChange, onActivate, placeholder, readOnly, multiline, className, inputRef, spellCheck = false, allowEmpty = true}: EditableProps): ReactElement {
    const [draft, setDraft] = useState(value)
    const [editing, setEditing] = useState(false)
    const localRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null)
    const ref = (inputRef ?? localRef) as RefObject<HTMLInputElement & HTMLTextAreaElement>

    useEffect(() => {
        if (!editing) {
            setDraft(value)
        }
    }, [value, editing])

    useEffect(() => {
        if (editing && ref.current) {
            ref.current.focus()
            ref.current.setSelectionRange(ref.current.value.length, ref.current.value.length)
        }
    }, [editing, ref])

    const commit = (): void => {
        setEditing(false)
        const next = draft.trim()
        if (!allowEmpty && !next) {
            setDraft(value)
            return
        }
        if (next !== value) {
            onChange(next)
        }
    }

    const cancel = (): void => {
        setEditing(false)
        setDraft(value)
    }

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (event.key === 'Enter' && (!multiline || event.metaKey || event.ctrlKey)) {
            event.preventDefault()
            commit()
        } else if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
            cancel()
        }
    }

    if (readOnly) {
        return (
            <div className={classnames('Editable truncate', className)} title={value}>
                {value || placeholder || ''}
            </div>
        )
    }

    if (!editing) {
        return (
            <div
                className={classnames('Editable cursor-text truncate', className, !value && 'opacity-50')}
                onClick={() => {
                    setDraft(value)
                    setEditing(true)
                    onActivate?.()
                }}
                role='presentation'
            >
                {value || placeholder || ' '}
            </div>
        )
    }

    const shared = {
        ref,
        className: classnames('Editable bg-transparent outline-none', className),
        value: draft,
        spellCheck,
        placeholder,
        onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDraft(event.target.value),
        onBlur: commit,
        onKeyDown,
    } as const

    if (multiline) {
        return (
            <textarea
                {...shared}
                rows={Math.min(12, Math.max(1, draft.split('\n').length))}
                className={classnames(shared.className, 'resize-none')}
            />
        )
    }
    return <input type='text' {...shared}/>
}
